import React, { useMemo } from 'react';

import { Loader } from '@opendoor/atoms/loader';

import {
  AsyncFeatureFlagGate,
  type AsyncFlagEvaluator,
  type DefaultAsyncFeatureFlagProps,
  useAsyncFlag,
} from '../flags/FeatureFlagGate';
import { ACCOUNT_FORM_FLAG, CLIENT_ORDERED_PROVIDERS_FLAG } from '../flags/flagKeys';
import { AccountFormV2, type AccountFormV2Props } from './AccountFormV2/AccountFormV2';
import { armExposure } from './experimentExposure';

type AccountFormContainerProps = {
  accountFormProps?: Pick<
    AccountFormV2Props,
    | 'redirectTo'
    | 'heading'
    | 'registrationHeading'
    | 'initialEmail'
    | 'onPasswordlessLoginRequest'
    | 'onRegistrationSuccess'
  >;
  featureFlagProps: DefaultAsyncFeatureFlagProps;
  /**
   * Evaluator for {@link CLIENT_ORDERED_PROVIDERS_FLAG}, which orders the
   * provider buttons by the seller's client. Its own prop rather than a member
   * of `accountFormProps`, and its result reaches **both** arms: the ordering is
   * independent of whatever variant the slot below is testing, so binding it to
   * one arm would make it a property of that experiment instead of a setting of
   * the surface.
   *
   * Omitted means off, and means no read at all — a surface that never wires
   * this renders the declared order without paying for a flag call.
   */
  clientOrderedProvidersFlagProps?: DefaultAsyncFeatureFlagProps;
};

/**
 * Stands in for an evaluator when no surface wired one, so the hook below keeps
 * an unconditional call site. Never invoked: it is only ever passed alongside
 * `skip`.
 */
const NO_EVALUATOR: AsyncFlagEvaluator = () => Promise.resolve(false);

/**
 * Both arms render V2, and that is deliberate — not a gate left half-wired.
 *
 * `ACCOUNT_FORM_FLAG` is a long-lived experiment slot for the account form,
 * not a one-shot migration switch: control holds the shipped baseline, and
 * treatment carries whatever variant is under test. Between experiments the
 * two coincide, which is what makes winding the rollout up or down invisible
 * to users. A variant goes in the `enabled` arm; when it wins it moves to
 * `disabled` and the slot reopens.
 *
 * So don't collapse the gate on the grounds that both arms match.
 *
 * The slot stands empty, so both arms render the shipped baseline. The next
 * variant goes in `enabled` and this comment names it.
 */
export const AccountForm: React.FC<AccountFormContainerProps> = ({
  accountFormProps,
  featureFlagProps,
  clientOrderedProvidersFlagProps,
}) => {
  const { flag = ACCOUNT_FORM_FLAG, forceDisabled = false, ...flagProps } = featureFlagProps;
  // `??`, not a destructuring default: a default fires on `undefined` only, and
  // the gate resolves a null loading node to the `disabled` arm. That would mount
  // the keyed control arm mid-read, report a control exposure, then flip to
  // treatment and report again — one seller in both arms, which is the single
  // corruption this module exists to prevent. It would also paint the unbadged
  // stack and reorder it under the seller.
  const loading = featureFlagProps.loading ?? <Loader size="sm" />;
  // Derived from the flag the gate resolved, not from the module default it
  // falls back to: `featureFlagProps.flag` can repoint the gate, and a seller
  // bucketed by one experiment but reported under another is a corruption
  // nothing downstream can see. Control first, matching declaration order.
  const [controlExposure, treatmentExposure] = useMemo(
    () => [
      armExposure({ experimentName: flag, variant: 'control' }),
      armExposure({ experimentName: flag, variant: 'treatment' }),
    ],
    [flag],
  );
  const {
    flag: clientOrderingFlag = CLIENT_ORDERED_PROVIDERS_FLAG,
    evaluate: evaluateClientOrdering = NO_EVALUATOR,
    forceDisabled: clientOrderingPinnedOff = false,
  } = clientOrderedProvidersFlagProps ?? {};
  // Read beside the gate rather than in a gate nested under it: a nested gate
  // could not start this read until the arm above had resolved, so the two waits
  // would stack ahead of first paint. Both start on this render instead, and the
  // arms below hold the same loader until whichever finishes last.
  const clientOrdering = useAsyncFlag(
    clientOrderingFlag,
    evaluateClientOrdering,
    clientOrderingPinnedOff || clientOrderedProvidersFlagProps === undefined,
  );
  // The order has to be settled before the form paints, not after: the provider
  // resolves it once at mount, and a late flip would move live buttons under a
  // cursor already travelling toward one of them.
  const holdForClientOrdering = (arm: React.ReactNode): React.ReactNode =>
    clientOrdering.loading ? loading : arm;
  return (
    <AsyncFeatureFlagGate
      flag={flag}
      // Distinct keys, not decoration: both arms are the same component at the
      // same position, so without them React reconciles in place and the
      // provider's mount-time resolution survives an arm switch — a kill
      // switch would leave the outgoing variant rendering.
      disabled={holdForClientOrdering(
        <AccountFormV2
          key="control"
          {...accountFormProps}
          clientOrderedProviders={clientOrdering.enabled}
          // A pinned arm reports nothing: `forceDisabled` renders this arm
          // without reading the flag, so the seller was never randomised. The
          // surface that pins it is reception's retired contact step, a funnel
          // that cannot reach treatment at all — its rows would put a population
          // in control that treatment can never hold.
          experimentExposure={forceDisabled ? undefined : controlExposure}
        />,
      )}
      enabled={holdForClientOrdering(
        <AccountFormV2
          key="treatment"
          {...accountFormProps}
          clientOrderedProviders={clientOrdering.enabled}
          experimentExposure={treatmentExposure}
        />,
      )}
      loading={loading}
      forceDisabled={forceDisabled}
      {...flagProps}
    />
  );
};
